
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@supabase/auth-helpers-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { PricingTier } from "./PricingTier";
import { ChurchContactForm } from "./ChurchContactForm";
import { PremiumBadge } from "./PremiumBadge";
import { useSubscriptionStatus } from "./hooks/useSubscriptionStatus";
import { usePremiumUpgrade } from "./hooks/usePremiumUpgrade";
import { usePricingTiers } from "./hooks/usePricingTiers";
import { useMediaQuery } from "@/hooks/use-mobile";

interface PricingDisplayProps {
  context?: 'welcome' | 'settings';
  onFreeClick?: () => void;
  onSubmit?: () => Promise<void>;
  formValues?: any;
}

export function PricingDisplay({ 
  context = 'settings',
  onFreeClick,
  onSubmit,
  formValues 
}: PricingDisplayProps) {
  const session = useSession();
  const navigate = useNavigate();
  const [showChurchContactForm, setShowChurchContactForm] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  
  const { isLoadingStatus, subscriptionStatus } = useSubscriptionStatus();
  const { isLoading, handleUpgrade } = usePremiumUpgrade();

  // Handle welcome context with form submission
  const handleWelcomeUpgrade = async () => {
    window.scrollTo(0, 0);
    
    if (context === 'welcome' && onSubmit) {
      await onSubmit();
    } else {
      await handleUpgrade();
    }
  };

  const handleChurchContact = async (): Promise<void> => {
    return new Promise<void>((resolve) => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      setShowChurchContactForm(true);
      resolve();
    });
  };

  const pricingTiers = usePricingTiers({
    context,
    subscriptionStatus,
    isLoading,
    isLoadingStatus,
    handleUpgrade: handleWelcomeUpgrade,
    onFreeClick,
    onChurchContact: handleChurchContact
  });

  const handleCloseChurchForm = () => {
    setShowChurchContactForm(false);
  };

  return (
    <div className="space-y-6 w-full animate-fade-in">
      {subscriptionStatus === 'premium' && <PremiumBadge />}

      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold text-app-text-primary">Choose Your Plan</h2>
        <p className="text-app-text-secondary">Select the plan that best fits your needs</p>
      </div>

      <div className={`grid gap-6 ${context === 'welcome' ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
        {pricingTiers.map((tier, index) => (
          <PricingTier key={index} {...tier} />
        ))}
      </div>

      {isMobile ? (
        // Use Sheet for mobile (full screen)
        <Sheet 
          open={showChurchContactForm} 
          onOpenChange={(open) => {
            setShowChurchContactForm(open);
            if (open) {
              window.scrollTo(0, 0);
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
            }
          }}
        >
          <SheetContent side="bottom" className="h-[100dvh] p-0 sm:max-w-none w-full">
            <ChurchContactForm onClose={handleCloseChurchForm} />
          </SheetContent>
        </Sheet>
      ) : (
        // Use Dialog for desktop
        <Dialog 
          open={showChurchContactForm} 
          onOpenChange={(open) => {
            setShowChurchContactForm(open);
            if (open) {
              window.scrollTo(0, 0);
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
            }
          }}
        >
          <DialogContent className="max-w-md sm:max-w-lg md:max-w-xl">
            <ChurchContactForm onClose={handleCloseChurchForm} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
